import type {
  Amount,
  DistributionRow,
  DrinkingPace,
  DrunkByYear,
  SpendYear,
} from "@corklog/shared";
import { useCallback, useId, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import { fetchStatistics } from "../api.js";
import { countryName } from "../countries.js";
import { useSeesPrices } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { Select } from "../components/controls.js";
import { locale } from "../locale.js";
import { useReload } from "../offline.js";

/**
 * Task 8.11 — what the cellar adds up to.
 *
 * Bottles by country, region, type and vintage decade; what is held, and what
 * was spent per year. The scope has asked for this since release 2 was
 * sketched, and the interesting part of building it was not the arithmetic.
 *
 * **It is an inventory, not a valuation**, and the page says so in as many
 * words. `project-scope.md` is explicit: the price recorded is what was paid,
 * what the collection is worth today is between its owner and their insurer,
 * and the application must not appear to have an opinion about it. A page
 * headed *Källarens värde* with one large number on it would have exactly that
 * opinion whatever the small print said.
 *
 * **Nothing is converted**, so every total is per currency. One rate and one
 * date have to be chosen to convert, and every choice is wrong in some view.
 *
 * **What the numbers do not cover is on the page, not in a footnote.** On this
 * cellar it is a third of the record: 37 of 176 owned bottles came with no
 * price, and 21 priced lots carry no purchase date. A value quietly covering
 * seventy per cent of the bottles, presented as the cellar's value, would be
 * the most misleading thing this application could show — and the gap is a
 * fact about the spreadsheet these rows came out of, not a bug.
 *
 * ## Quiet bars
 *
 * `design-direction.md` spends the boldness in one place and it is the
 * drinking-window band. So a distribution is a row of text with a hairline
 * under it, scaled to the count, in the same colour as every other rule on the
 * page. It reads as a measure rather than as a chart, and it does not compete
 * with the one loud element in the interface.
 */

/**
 * Kronor, rounded to the krona. The öre are exact in the sum and are noise on
 * a page whose numbers are in the tens of thousands — and a total shown to the
 * öre would look like a valuation, which is the one thing this page is not.
 *
 * Built when it is read rather than once, because the locale follows the
 * interface's language and that can change while the module stays loaded.
 */
const money = {
  format: (amount: number) =>
    new Intl.NumberFormat(locale(), { maximumFractionDigits: 0 }).format(amount),
};

/**
 * *1 flaska, 1 vin* rather than *1 flaskor, 1 viner*.
 *
 * i18next chooses a plural form from one `count`, and these sentences hold two
 * numbers that are singular independently — a row of one Rosé is nine bottles
 * of one wine. So each number is put into words on its own and the sentence
 * only places them, which is also why `holding_one` could never have worked:
 * it was keyed on a count the call never passed.
 */
function counted(t: TFunction, bottles: number, wines: number) {
  return {
    bottles: t("statistics.bottles", { count: bottles }),
    wines: t("statistics.wines", { count: wines }),
  };
}

/**
 * One distribution: a heading, the rows, and a hairline under each scaled to
 * the largest. The bar is drawn from the bottle count because that is what the
 * rows are ordered by — a measure that disagreed with the order would be two
 * claims about the same list.
 */
function Distribution({
  title,
  rows,
  label,
}: {
  title: string;
  rows: readonly DistributionRow[];
  label: (key: string) => string;
}) {
  const { t } = useTranslation();
  const most = rows.reduce((top, row) => Math.max(top, row.bottles), 0);
  if (rows.length === 0) return null;

  return (
    <section className="mt-10">
      <h2 className="font-display text-ink text-base">{title}</h2>
      <ul className="mt-4">
        {rows.map((row) => (
          <li key={row.key} className="py-2.5">
            <div className="flex items-baseline justify-between gap-4">
              <span className="text-ink text-sm">{label(row.key)}</span>
              <span className="text-muted shrink-0 text-sm tabular-nums">
                {t("statistics.bottlesAndWines", counted(t, row.bottles, row.wines))}
              </span>
            </div>
            <div
              aria-hidden
              className="bg-line mt-2 h-px"
              style={{ width: `${most === 0 ? 0 : (row.bottles / most) * 100}%` }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}

type State =
  | { status: "loading" }
  | { status: "ready"; statistics: Awaited<ReturnType<typeof fetchStatistics>> }
  | { status: "failed" };

export function Statistics() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  const seesPrices = useSeesPrices();

  const load = useCallback(() => {
    void fetchStatistics(cellarId)
      .then((statistics) => setState({ status: "ready", statistics }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="statistics" />
      {/* The ordinary sentence promises what the cellar cost, which is a
          promise this page does not keep for a reader who may not see prices.
          The money further down says it is not shown; a heading that had
          already offered it would read as the page having failed to load it. */}
      <p className="text-muted mt-2 max-w-prose text-sm">
        {t(seesPrices === false ? "statistics.introWithoutPrices" : "statistics.intro")}
      </p>

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("wines.loadFailed")}
        </p>
      )}

      {state.status === "ready" && (
        <>
          <p className="text-ink mt-8 max-w-prose">
            {t(
              "statistics.holding",
              counted(t, state.statistics.bottlesInCellar, state.statistics.wines),
            )}
            {state.statistics.bottlesAway > 0 && (
              <> {t("statistics.away", { count: state.statistics.bottlesAway })}</>
            )}
          </p>

          {state.statistics.pace !== null && <Pace pace={state.statistics.pace} />}

          <Distribution
            title={t("statistics.byCountry")}
            rows={state.statistics.byCountry}
            label={(key) => countryName(key)}
          />
          <Distribution
            title={t("statistics.byType")}
            rows={state.statistics.byType}
            label={(key) => t(`wineType.${key}`)}
          />
          <Distribution
            title={t("statistics.byRegion")}
            rows={state.statistics.byRegion}
            // Regions are the cellar's own rows and are already in its own
            // words, so there is nothing to translate.
            label={(key) => key}
          />
          <Distribution
            title={t("statistics.byDecade")}
            rows={state.statistics.byDecade}
            label={(key) => t("statistics.decade", { decade: key })}
          />

          {state.statistics.drunk !== null && <Drunk drunk={state.statistics.drunk} />}

          <Money
            value={state.statistics.value}
            spend={state.statistics.spend}
            silence={state.statistics.silence}
          />
        </>
      )}
    </section>
  );
}

/** `2026-09-11` as *11 september 2026*, read as the day it names wherever the
 *  browser is — hence UTC, which is where a bare date parses to. */
function longDay(day: string): string {
  return new Date(`${day}T00:00:00Z`).toLocaleDateString(locale(), {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** A day `n` days after another, as `YYYY-MM-DD`. */
function daysAfter(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + n * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

const years = {
  format: (value: number) =>
    new Intl.NumberFormat(locale(), { maximumFractionDigits: 1 }).format(value),
};

/**
 * Task 10.20 — how fast the cellar is drunk, and whether that is fast enough.
 *
 * The arithmetic and every rule it follows are the server's; see `pace.ts` and
 * the decision of 2026-09-26. What the screen owns is the order the sentences
 * come in — what was drunk, what is ready, and then the one line that is the
 * point — and that the shortfall is never said without the assumption that
 * makes it a floor. Prose rather than a chart, for the reason the bars on this
 * page are hairlines: the drinking-window band is the loud thing here.
 */
function Pace({ pace }: { pace: DrinkingPace }) {
  const { t } = useTranslation();
  const perYear = pace.perYear === null ? null : Math.round(pace.perYear);

  return (
    <section className="mt-10">
      <h2 className="font-display text-ink text-base">{t("statistics.pace.title")}</h2>
      <p className="text-ink mt-4 max-w-prose text-sm">
        {t("statistics.pace.drunk", { count: pace.drunk, since: longDay(pace.since) })}{" "}
        {perYear === null
          ? t("statistics.pace.tooShort", {
              // The first day the span reaches ninety, which is the day the
              // server will start saying a pace — `PACE_MINIMUM_DAYS`.
              date: longDay(daysAfter(pace.since, 89)),
            })
          : t("statistics.pace.perYear", { count: perYear })}
      </p>

      <p className="text-ink mt-3 max-w-prose text-sm">
        {t("statistics.pace.ready", { count: pace.ready })}
        {pace.perYear !== null && pace.perYear > 0 && pace.ready > 0 && (
          <>
            {" "}
            {t("statistics.pace.lasts", {
              years: years.format(pace.ready / pace.perYear),
            })}
          </>
        )}
        {pace.past > 0 && <> {t("statistics.pace.past", { count: pace.past })}</>}
      </p>

      {pace.perYear !== null && (
        <p className="text-ink mt-3 max-w-prose text-sm">
          {pace.shortfall === null
            ? t("statistics.pace.enough")
            : t("statistics.pace.shortfall", pace.shortfall)}{" "}
          <span className="text-muted">{t("statistics.pace.kindest")}</span>
        </p>
      )}

      {pace.withoutWindow > 0 && (
        <p className="text-muted mt-3 max-w-prose text-sm">
          {t("statistics.pace.withoutWindow", { count: pace.withoutWindow })}
        </p>
      )}
    </section>
  );
}

/**
 * Task 10.22 — what was drunk, one year at a time.
 *
 * The four distributions above, drawn again for what left the cellar down a
 * throat, with the same `Distribution` so the two halves of the page read as
 * one question asked twice. One year on screen rather than every year, because
 * four distributions a year is a page that grows by a screen every January;
 * the newest is shown and the others are one choice away, and the choice is
 * not drawn until there is a second year to choose.
 *
 * **The edge of the book is said, not hidden.** What the spreadsheet had drunk
 * before the import has a count and no day, so it is in no year — and a page
 * that quietly left it out would make the first year look like the first
 * year's drinking.
 */
function Drunk({ drunk }: { drunk: DrunkByYear }) {
  const { t } = useTranslation();
  const yearId = useId();
  const [chosen, setChosen] = useState<number | null>(null);
  const shown = drunk.years.find((year) => year.year === chosen) ?? drunk.years[0];
  const since = longDay(drunk.since);

  return (
    <section className="mt-12">
      <h2 className="font-display text-ink text-lg">{t("statistics.drunk.title")}</h2>
      <p className="text-muted mt-1 max-w-prose text-sm">
        {t("statistics.drunk.what", { since })}
      </p>

      {shown === undefined ? (
        <p className="text-ink mt-4 max-w-prose text-sm">
          {t("statistics.drunk.none", { since })}
        </p>
      ) : (
        <>
          {drunk.years.length > 1 && (
            <div className="mt-4 max-w-40">
              <label className="text-muted block text-sm" htmlFor={yearId}>
                {t("statistics.drunk.year")}
              </label>
              <div className="mt-1">
                <Select
                  id={yearId}
                  value={String(shown.year)}
                  onChange={(next) => setChosen(Number(next))}
                >
                  {drunk.years.map((year) => (
                    <option key={year.year} value={year.year}>
                      {year.year}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}

          <p className="text-ink mt-4 max-w-prose">
            {t("statistics.drunk.total", {
              year: shown.year,
              ...counted(t, shown.bottles, shown.wines),
            })}
          </p>

          <Distribution
            title={t("statistics.byCountry")}
            rows={shown.byCountry}
            label={(key) => countryName(key)}
          />
          <Distribution
            title={t("statistics.byType")}
            rows={shown.byType}
            label={(key) => t(`wineType.${key}`)}
          />
          <Distribution
            title={t("statistics.byRegion")}
            rows={shown.byRegion}
            label={(key) => key}
          />
          <Distribution
            title={t("statistics.byDecade")}
            rows={shown.byDecade}
            label={(key) => t("statistics.decade", { decade: key })}
          />
        </>
      )}

      {drunk.beforeTheBook > 0 && (
        <p className="text-muted mt-6 max-w-prose text-sm">
          {t("statistics.drunk.beforeTheBook", { count: drunk.beforeTheBook })}
        </p>
      )}
    </section>
  );
}

/**
 * The money, or the sentence that says it is not being shown.
 *
 * Null rather than an empty list is what carries that difference — of the two
 * ways to be wrong about somebody else's prices, saying less is the
 * recoverable one, and `project-scope.md` decided it that way for guests long
 * before there were any.
 */
function Money({
  value,
  spend,
  silence,
}: {
  value: readonly Amount[] | null;
  spend: readonly SpendYear[] | null;
  silence: { bottlesWithoutPrice: number; pricedLotsWithoutDate: number; winesWithoutVintage: number }; // prettier-ignore
}) {
  const { t } = useTranslation();

  if (value === null || spend === null) {
    return (
      <p className="text-muted mt-10 max-w-prose text-sm">{t("statistics.hidden")}</p>
    );
  }

  const most = spend.reduce((top, row) => Math.max(top, row.amount), 0);

  return (
    <>
      <section className="mt-10">
        <h2 className="font-display text-ink text-base">{t("statistics.value")}</h2>
        <p className="text-muted mt-1 max-w-prose text-sm">{t("statistics.valueWhat")}</p>
        {value.length === 0 ? (
          <p className="text-muted mt-4 text-sm">{t("statistics.noPrices")}</p>
        ) : (
          <ul className="mt-4">
            {value.map((row) => (
              <li
                key={row.currency}
                className="flex items-baseline justify-between py-2.5"
              >
                <span className="font-display text-ink text-lg tabular-nums">
                  {money.format(row.amount)} {row.currency}
                </span>
                <span className="text-muted shrink-0 text-sm">
                  {t("statistics.overBottles", { count: row.bottles })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {spend.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-ink text-base">{t("statistics.spend")}</h2>
          <p className="text-muted mt-1 max-w-prose text-sm">
            {t("statistics.spendWhat")}
          </p>
          <ul className="mt-4">
            {spend.map((row) => (
              <li key={`${row.year}-${row.currency}`} className="py-2.5">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-ink text-sm tabular-nums">{row.year}</span>
                  <span className="text-muted shrink-0 text-sm tabular-nums">
                    {money.format(row.amount)} {row.currency} ·{" "}
                    {t("statistics.overBottles", { count: row.bottles })}
                  </span>
                </div>
                <div
                  aria-hidden
                  className="bg-line mt-2 h-px"
                  style={{ width: `${most === 0 ? 0 : (row.amount / most) * 100}%` }}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      <Silence silence={silence} />
    </>
  );
}

/** What the numbers above do not cover, said rather than left to be noticed. */
function Silence({
  silence,
}: {
  silence: { bottlesWithoutPrice: number; pricedLotsWithoutDate: number; winesWithoutVintage: number }; // prettier-ignore
}) {
  const { t } = useTranslation();
  const said = [
    silence.bottlesWithoutPrice > 0
      ? t("statistics.silence.noPrice", { count: silence.bottlesWithoutPrice })
      : null,
    silence.pricedLotsWithoutDate > 0
      ? t("statistics.silence.noDate", { count: silence.pricedLotsWithoutDate })
      : null,
    silence.winesWithoutVintage > 0
      ? t("statistics.silence.noVintage", { count: silence.winesWithoutVintage })
      : null,
  ].filter((line) => line !== null);

  if (said.length === 0) return null;

  return (
    <p className="text-muted mt-10 max-w-prose text-sm">
      {said.join(" ")} {t("statistics.silence.why")}
    </p>
  );
}
